import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import leaveTypeService from '../../services/leaveTypeService';

// Async thunks
export const fetchLeaveTypes = createAsyncThunk(
  'leaveTypes/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveTypeService.getAll();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave types');
    }
  }
);

export const fetchOrgLeaveTypes = createAsyncThunk(
  'leaveTypes/fetchOrg',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveTypeService.getOrgLeaveTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch organization leave types');
    }
  }
);

export const fetchLeaveTypeById = createAsyncThunk(
  'leaveTypes/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await leaveTypeService.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave type');
    }
  }
);

export const createLeaveType = createAsyncThunk(
  'leaveTypes/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await leaveTypeService.create(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create leave type');
    }
  }
);

export const updateLeaveType = createAsyncThunk(
  'leaveTypes/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await leaveTypeService.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update leave type');
    }
  }
);

export const deleteLeaveType = createAsyncThunk(
  'leaveTypes/delete',
  async (id, { rejectWithValue }) => {
    try {
      await leaveTypeService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete leave type');
    }
  }
);

// Slice
const leaveTypeSlice = createSlice({
  name: 'leaveTypes',
  initialState: {
    list: [],
    orgList: [],
    current: null,
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all
    builder
      .addCase(fetchLeaveTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeaveTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchLeaveTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch org leave types
    builder
      .addCase(fetchOrgLeaveTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrgLeaveTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.orgList = action.payload;
      })
      .addCase(fetchOrgLeaveTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch by ID
    builder
      .addCase(fetchLeaveTypeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeaveTypeById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchLeaveTypeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create
    builder
      .addCase(createLeaveType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLeaveType.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
        state.orgList.unshift(action.payload);
      })
      .addCase(createLeaveType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update
    builder
      .addCase(updateLeaveType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeaveType.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        const orgIndex = state.orgList.findIndex((item) => item.id === action.payload.id);
        if (orgIndex !== -1) {
          state.orgList[orgIndex] = action.payload;
        }
      })
      .addCase(updateLeaveType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete
    builder
      .addCase(deleteLeaveType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLeaveType.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter((item) => item.id !== action.payload);
        state.orgList = state.orgList.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteLeaveType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError } = leaveTypeSlice.actions;
export default leaveTypeSlice.reducer;
